import useDecodedToken from './useDecodedToken';

type PermissionAction = 'create' | 'read' | 'update' | 'delete' | 'assign_role' | 'read_role' | 'revoke_role';
type ServiceType =
  | 'credential'
  | 'device_data'
  | 'document'
  | 'entity'
  | 'evaluation'
  | 'input'
  | 'invitation'
  | 'member'
  | 'metrics'
  | 'organization'
  | 'resource'
  | 'supplier'
  | 'transaction'
  | 'user'
  | 'workflow'
  | 'role';

// Group permission types for easier access
export enum PermissionType {
  READ = 'read',
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  ASSIGN_ROLE = 'assign_role',
  READ_ROLE = 'read_role',
  REVOKE_ROLE = 'revoke_role',
}

// UI-focused permission helpers
export interface UIPermissions {
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canSubmit: boolean; // Combined create/update permission
  canManageRoles: boolean; // Combined role management permissions
}

interface UsePermissionsReturn {
  hasPermission: (action: PermissionAction, service: ServiceType) => boolean;
  hasAnyPermission: (service: ServiceType) => boolean;
  getAllPermissionsForService: (service: ServiceType) => string[];
  hasPermissionType: (type: PermissionType, service: ServiceType) => boolean;
  getPermissionTypes: (service: ServiceType) => PermissionType[];
  getUIPermissions: (service: ServiceType) => UIPermissions;
  canPerformAction: (service: ServiceType, action: 'view' | 'create' | 'edit' | 'delete' | 'submit') => boolean;
  permissions: string[];
  hasAllPermissions: boolean;
}

/**
 * Hook to check user permissions based on their decoded JWT token
 * Used for implementing RBAC (Role Based Access Control)
 */
const usePermissions = (): UsePermissionsReturn => {
  const { decodedToken } = useDecodedToken();

  // Extract permissions array from the decoded token
  const rawPermissions: string[] = (decodedToken.permissions as string[]) || [];

  // If permissions array is empty, user has all permissions
  const hasAllPermissions = rawPermissions.length === 0;

  // Use actual permissions or mock all permissions if empty
  const permissions = hasAllPermissions ? generateAllPermissionsArray() : rawPermissions;

  /**
   * Generate an array containing all possible permission combinations
   * Used when permissions array is empty (indicating all permissions are granted)
   */
  function generateAllPermissionsArray(): string[] {
    const allPermissions: string[] = [];
    const actions: PermissionAction[] = [
      'create',
      'read',
      'update',
      'delete',
      'assign_role',
      'read_role',
      'revoke_role',
    ];
    const services: ServiceType[] = [
      'credential',
      'device_data',
      'document',
      'entity',
      'evaluation',
      'input',
      'invitation',
      'member',
      'metrics',
      'organization',
      'resource',
      'supplier',
      'transaction',
      'user',
      'workflow',
      'role',
    ];

    // For all service types, generate all possible permissions
    services.forEach(service => {
      actions.forEach(action => {
        allPermissions.push(`${action}:${service}`);
      });
    });

    return allPermissions;
  }

  /**
   * Check if a wildcard permission exists for the service
   * @param service - The service to check for wildcard permissions
   * @returns boolean indicating if a wildcard permission exists
   */
  const hasWildcardPermission = (service: ServiceType): boolean => {
    return permissions.some(permission => permission === `*:${service}`);
  };

  /**
   * Check if user has a specific permission for a service
   * @param action - The permission action (create, read, update, delete, etc.)
   * @param service - The service to check permissions for
   * @returns boolean indicating if the user has the permission
   */
  const hasPermission = (action: PermissionAction, service: ServiceType): boolean => {
    // If permissions array is empty, user has all permissions
    if (hasAllPermissions) return true;

    // Check if wildcard permission exists for this service
    if (hasWildcardPermission(service)) return true;

    const permissionString = `${action}:${service}`;
    return permissions.includes(permissionString);
  };

  /**
   * Check if user has any permission related to a service
   * @param service - The service to check permissions for
   * @returns boolean indicating if the user has any permission for the service
   */
  const hasAnyPermission = (service: ServiceType): boolean => {
    // If permissions array is empty, user has all permissions
    if (hasAllPermissions) return true;

    // Check if wildcard permission exists for this service
    if (hasWildcardPermission(service)) return true;

    return permissions.some(permission => permission.endsWith(`:${service}`));
  };

  /**
   * Get all permissions the user has for a specific service
   * @param service - The service to get permissions for
   * @returns array of permissions the user has for the service
   */
  const getAllPermissionsForService = (service: ServiceType): string[] => {
    // If there's a wildcard permission, return all possible actions for this service
    if (hasWildcardPermission(service)) {
      const actions: PermissionAction[] = [
        'create',
        'read',
        'update',
        'delete',
        'assign_role',
        'read_role',
        'revoke_role',
      ];
      return actions.map(action => `${action}:${service}`);
    }

    return permissions.filter(permission => permission.endsWith(`:${service}`));
  };

  /**
   * Check if user has a specific type of permission for a service
   * @param type - The permission type (read, create, update, delete, assign_role, etc.)
   * @param service - The service to check permissions for
   * @returns boolean indicating if the user has the permission type
   */
  const hasPermissionType = (type: PermissionType, service: ServiceType): boolean => {
    // If permissions array is empty, user has all permissions
    if (hasAllPermissions) return true;

    // Check if wildcard permission exists for this service
    if (hasWildcardPermission(service)) return true;

    switch (type) {
      case PermissionType.READ:
        return hasPermission('read', service);
      case PermissionType.CREATE:
        return hasPermission('create', service);
      case PermissionType.UPDATE:
        return hasPermission('update', service);
      case PermissionType.DELETE:
        return hasPermission('delete', service);
      case PermissionType.ASSIGN_ROLE:
        return hasPermission('assign_role', service);
      case PermissionType.READ_ROLE:
        return hasPermission('read_role', service);
      case PermissionType.REVOKE_ROLE:
        return hasPermission('revoke_role', service);
      default:
        return false;
    }
  };

  /**
   * Get all permission types the user has for a service
   * @param service - The service to get permission types for
   * @returns array of permission types the user has for the service
   */
  const getPermissionTypes = (service: ServiceType): PermissionType[] => {
    // If permissions array is empty, user has all permissions
    if (hasAllPermissions) {
      return Object.values(PermissionType);
    }

    // If wildcard permission exists for this service, return all permission types
    if (hasWildcardPermission(service)) {
      return Object.values(PermissionType);
    }

    const types: PermissionType[] = [];

    if (hasPermissionType(PermissionType.READ, service)) {
      types.push(PermissionType.READ);
    }

    if (hasPermissionType(PermissionType.CREATE, service)) {
      types.push(PermissionType.CREATE);
    }

    if (hasPermissionType(PermissionType.UPDATE, service)) {
      types.push(PermissionType.UPDATE);
    }

    if (hasPermissionType(PermissionType.DELETE, service)) {
      types.push(PermissionType.DELETE);
    }

    if (hasPermissionType(PermissionType.ASSIGN_ROLE, service)) {
      types.push(PermissionType.ASSIGN_ROLE);
    }

    if (hasPermissionType(PermissionType.READ_ROLE, service)) {
      types.push(PermissionType.READ_ROLE);
    }

    if (hasPermissionType(PermissionType.REVOKE_ROLE, service)) {
      types.push(PermissionType.REVOKE_ROLE);
    }

    return types;
  };

  /**
   * Get UI-focused permissions for a service
   * Convenient for controlling UI elements like buttons, forms, etc.
   * @param service - The service to get UI permissions for
   * @returns Object with boolean flags for common UI permission scenarios
   */
  const getUIPermissions = (service: ServiceType): UIPermissions => {
    // If permissions array is empty, user has all UI permissions
    if (hasAllPermissions) {
      return {
        canView: true,
        canCreate: true,
        canUpdate: true,
        canDelete: true,
        canSubmit: true,
        canManageRoles: true,
      };
    }

    // If wildcard permission exists for this service, grant all UI permissions
    if (hasWildcardPermission(service)) {
      return {
        canView: true,
        canCreate: true,
        canUpdate: true,
        canDelete: true,
        canSubmit: true,
        canManageRoles: true,
      };
    }

    const canCreate = hasPermissionType(PermissionType.CREATE, service);
    const canUpdate = hasPermissionType(PermissionType.UPDATE, service);

    return {
      canView: hasPermissionType(PermissionType.READ, service),
      canCreate,
      canUpdate,
      canDelete: hasPermissionType(PermissionType.DELETE, service),
      canSubmit: canCreate || canUpdate, // Can submit if either create or update is allowed
      canManageRoles:
        hasPermissionType(PermissionType.ASSIGN_ROLE, service) ||
        hasPermissionType(PermissionType.REVOKE_ROLE, service),
    };
  };

  /**
   * Check if user can perform a specific high-level action on a service
   * Simplified interface for common UI permission checks
   * @param service - The service to check permissions for
   * @param action - The high-level action to check (view, create, edit, delete, submit)
   * @returns boolean indicating if the user can perform the action
   */
  const canPerformAction = (
    service: ServiceType,
    action: 'view' | 'create' | 'edit' | 'delete' | 'submit',
  ): boolean => {
    // If permissions array is empty, user can perform all actions
    if (hasAllPermissions) return true;

    // If wildcard permission exists for this service, allow all actions
    if (hasWildcardPermission(service)) return true;

    switch (action) {
      case 'view':
        return hasPermissionType(PermissionType.READ, service);
      case 'create':
        return hasPermissionType(PermissionType.CREATE, service);
      case 'edit':
        return hasPermissionType(PermissionType.UPDATE, service);
      case 'delete':
        return hasPermissionType(PermissionType.DELETE, service);
      case 'submit':
        return hasPermissionType(PermissionType.CREATE, service) || hasPermissionType(PermissionType.UPDATE, service);
      default:
        return false;
    }
  };

  return {
    hasPermission,
    hasAnyPermission,
    getAllPermissionsForService,
    hasPermissionType,
    getPermissionTypes,
    getUIPermissions,
    canPerformAction,
    permissions,
    hasAllPermissions,
  };
};

export default usePermissions;
